/**
 * When your turn starts: the banner and chime fire once per encounter turn, however many views
 * stream in during it, and the map and turn bar stay lit for as long as it lasts. Outside encounters
 * there are no turns (the dungeon keeps time in rounds instead), so nothing is announced or lit.
 */

import type { PlayerView } from '../shared/view.ts';

/** Whose turn the board shows: yours in an encounter, or nobody's. */
export type TurnGlow = 'combat' | null;

/** Lit while it's your encounter turn. */
export function turnGlow(view: PlayerView | null): TurnGlow {
  if (!view || view.phase !== 'dungeon' || !view.combat) return null;
  return view.combat.yourTurn ? 'combat' : null;
}

/**
 * Decides when to announce your encounter turn. A turn is keyed by its round and whose turn it is,
 * so a view that briefly says it isn't your turn (a reaction, a prompt) doesn't announce it again
 * when the next one says it is. Not by its place in the order: the view drops the dead from it, so
 * killing someone ahead of you would move your place mid-turn.
 */
export class TurnAlert {
  private combatKey: string | null = null;

  /** What to announce for this view: 'combat' or nothing. Call it once per view. */
  next(view: PlayerView): 'combat' | null {
    const c = view.phase === 'dungeon' ? view.combat : null;
    // An encounter's end forgets its turns, so the next encounter's first round is news again.
    if (!c) {
      this.combatKey = null;
      return null;
    }
    if (!c.yourTurn) return null;
    const key = `${c.round}:${c.currentId}`;
    if (key === this.combatKey) return null;
    this.combatKey = key;
    return 'combat';
  }
}
